// Global Instructions Rule Applied!
//
// useInvestorForm — the state of the Investor form: the three reads (the
// investor, its commitments, the select lists), a draft the fields edit, and
// the explicit Create / Save.
//
// Edit mode sends only the fields that differ from what was loaded
// (investors__save leaves an absent key alone and clears a key sent as null).
// Create mode sends what was filled in. On Create the "new" tab morphs into
// the investor's edit tab in place (the New Fund form precedent).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { summariseFieldErrors } from '../../../core/utils/utilities.ts'
import { isRequiredFieldValid } from '../../../core/components/InputManager/utils/formatters.ts'
import type { FieldConfig, FieldValue } from '../../../core/components/InputManager/types.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type { RootState } from '../../../state_manager/store.ts'
import { TABLE_INVESTORS } from '../../../state_manager/globals.ts'
import { transformFormTab, updateTabTitle } from '../../../store/slices/tabSlice'
import {
  InvestorDetailFields,
  type InvestorCommitmentRow,
  type InvestorDetail,
  type InvestorFormOptions
} from '../../../state_manager/data_models/investor.ts'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { fetchCompanyById } from '../../companies/services/companiesService.ts'
import {
  fetchInvestorCommitments,
  fetchInvestorDetail,
  fetchInvestorFormOptions,
  saveInvestor,
  type InvestorSavePayload
} from '../services/investorsService.ts'
import {
  EMPTY_INVESTOR_DRAFT,
  getAccountLinkFields,
  getClassificationFields,
  getInvestorFields,
  getNoticeFields,
  type InvestorDraft,
  type InvestorFieldOptions
} from '../utils/investorFieldConfigs.ts'

const VALIDATION_TOAST_KEY = 'investor-form-required'
const EMPTY_OPTIONS: SelectListEntry[] = []

const draftFromDetail = (detail: InvestorDetail): InvestorDraft => ({
  investors__name: detail.investors__name,
  investors__legal_name: detail.investors__legal_name,
  investors__investor_number: detail.investors__investor_number,
  accounts__id: detail.accounts__id
    ? { value: detail.accounts__id, label: detail.accounts__name ?? '' }
    : null,
  investor_classifications__id: detail.investor_classifications__id,
  legal_entity_types__id: detail.legal_entity_types__id,
  sbic_investor_types__id: detail.sbic_investor_types__id,
  prog__notice_supplement_template_ids: [...detail.prog__notice_supplement_template_ids]
})

const text = (value: string | null): string | null => {
  const trimmed = (value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

/** The same set of ids, in any order. */
const sameIds = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  const sortedB = [...b].sort((x, y) => x - y)
  return [...a].sort((x, y) => x - y).every((id, i) => id === sortedB[i])
}

/** The company select hands back an option object, an id, or nothing. */
const toAccountValue = (value: FieldValue): InvestorDraft['accounts__id'] => {
  if (value && typeof value === 'object' && !Array.isArray(value) && value.value) {
    return { value: String(value.value), label: String(value.label ?? value.name ?? '') }
  }
  if (typeof value === 'string' && value) return { value, label: '' }
  return null
}

/** What an edit sends: only the fields that differ from the loaded record. */
export const buildInvestorEditPayload = (
  id: string,
  draft: InvestorDraft,
  baseline: InvestorDraft
): InvestorSavePayload => {
  const payload: InvestorSavePayload = { id }
  if (text(draft.investors__name) !== text(baseline.investors__name)) {
    payload.name = text(draft.investors__name) ?? ''
  }
  if (text(draft.investors__legal_name) !== text(baseline.investors__legal_name)) {
    payload.legal_name = text(draft.investors__legal_name)
  }
  if (text(draft.investors__investor_number) !== text(baseline.investors__investor_number)) {
    payload.investor_number = text(draft.investors__investor_number)
  }
  if (draft.investor_classifications__id !== baseline.investor_classifications__id) {
    payload.investor_classification_id = draft.investor_classifications__id
  }
  if (draft.legal_entity_types__id !== baseline.legal_entity_types__id) {
    payload.legal_entity_type_id = draft.legal_entity_types__id
  }
  if (draft.sbic_investor_types__id !== baseline.sbic_investor_types__id) {
    payload.sbic_investor_type_id = draft.sbic_investor_types__id
  }
  if (!sameIds(draft.prog__notice_supplement_template_ids, baseline.prog__notice_supplement_template_ids)) {
    payload.notice_supplement_template_ids = draft.prog__notice_supplement_template_ids
  }
  return payload
}

/** What a create sends: the account, the name and whatever else was filled in. */
export const buildInvestorCreatePayload = (draft: InvestorDraft): InvestorSavePayload => {
  const payload: InvestorSavePayload = {
    account_id: draft.accounts__id?.value ?? '',
    name: text(draft.investors__name) ?? ''
  }
  const legalName = text(draft.investors__legal_name)
  if (legalName) payload.legal_name = legalName
  const investorNumber = text(draft.investors__investor_number)
  if (investorNumber) payload.investor_number = investorNumber
  if (draft.investor_classifications__id != null) payload.investor_classification_id = draft.investor_classifications__id
  if (draft.legal_entity_types__id != null) payload.legal_entity_type_id = draft.legal_entity_types__id
  if (draft.sbic_investor_types__id != null) payload.sbic_investor_type_id = draft.sbic_investor_types__id
  if (draft.prog__notice_supplement_template_ids.length > 0) {
    payload.notice_supplement_template_ids = draft.prog__notice_supplement_template_ids
  }
  return payload
}

export function useInvestorForm(recordId: string | null, isNew: boolean) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { closeFormTab } = useTabManagerContext()

  // New-record tabs are keyed per prefill query (tabSlice.addFormTab), so the
  // key carries the mount-time search string. Snapshot it: the global
  // location later reflects whichever tab is active, not this one.
  const [mountSearch] = useState(() => (isNew ? location.search : ''))
  const formTabKey = `${TABLE_INVESTORS}-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}${mountSearch}`
  // /investors/forms/full/new?account=<id>: the account is filled in and fixed
  const [prefillAccountId] = useState<string | null>(() =>
    isNew ? new URLSearchParams(location.search).get('account') : null
  )

  const classifications = useSelector((state: RootState) => state.globalConfigs?.investor_classifications)
  const sbicInvestorTypes = useSelector((state: RootState) => state.globalConfigs?.sbic_investor_types)

  const [detail, setDetail] = useState<InvestorDetail | null>(null)
  const [commitments, setCommitments] = useState<InvestorCommitmentRow[]>([])
  const [formOptions, setFormOptions] = useState<InvestorFormOptions | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [commitmentsLoading, setCommitmentsLoading] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [baseline, setBaseline] = useState<InvestorDraft>(EMPTY_INVESTOR_DRAFT)
  const [draft, setDraft] = useState<InvestorDraft>(EMPTY_INVESTOR_DRAFT)
  const [saving, setSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [validationErrorsVersion, setValidationErrorsVersion] = useState(0)
  // InputManager keeps its own copy of what was typed and ignores a prop
  // value for a field the user has edited. Putting the fields back (Cancel)
  // therefore remounts the InputManagers: the form keys them on this
  // (the Reports and Downloads forms' inputManagerKey precedent).
  const [resetVersion, setResetVersion] = useState(0)
  // a new investor is named after its account only until the user names it
  const nameTouchedRef = useRef(false)

  const applyDetail = useCallback(
    (next: InvestorDetail) => {
      const fresh = draftFromDetail(next)
      setDetail(next)
      setBaseline(fresh)
      setDraft(fresh)
      dispatch(updateTabTitle({ key: `${TABLE_INVESTORS}-form-edit-${next.investors__id}`, title: next.investors__name }))
    },
    [dispatch]
  )

  const loadCommitments = useCallback(async () => {
    if (isNew || !recordId) return
    setCommitmentsLoading(true)
    try {
      const res = await fetchInvestorCommitments(recordId, getAccessToken(store.getState().auth))
      if (res.error) showErrorToast(res.error)
      setCommitments(res.data)
    } finally {
      setCommitmentsLoading(false)
    }
  }, [isNew, recordId])

  // the select lists that are not in the global configs
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const res = await fetchInvestorFormOptions(getAccessToken(store.getState().auth))
      if (cancelled) return
      if (res.error) showErrorToast(res.error)
      setFormOptions(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Cold-load (the hard-refresh path is the normal path: there is no list
  // page that could have filled a slice first).
  useEffect(() => {
    let cancelled = false
    if (isNew) {
      dispatch(updateTabTitle({ key: formTabKey, title: 'New Investor' }))
      if (!prefillAccountId) return
      void (async () => {
        const res = await fetchCompanyById(prefillAccountId, getAccessToken(store.getState().auth))
        if (cancelled) return
        if (res.error || !res.data) {
          showErrorToast(res.error || 'The account could not be loaded')
          return
        }
        const account = {
          value: prefillAccountId,
          label: String(res.data[AccountRecordFields.accounts__name] ?? '')
        }
        // an untouched draft follows the account: a new investor entity is
        // usually named after it
        setBaseline((prev) => ({ ...prev, accounts__id: account, investors__name: account.label || null }))
        setDraft((prev) => ({
          ...prev,
          accounts__id: account,
          investors__name: prev.investors__name ?? (account.label || null)
        }))
      })()
      return () => {
        cancelled = true
      }
    }
    if (!recordId) return
    void (async () => {
      setLoading(true)
      setNotFound(false)
      setLoadError(null)
      const token = getAccessToken(store.getState().auth)
      const [detailRes, commitmentsRes] = await Promise.all([
        fetchInvestorDetail(recordId, token),
        fetchInvestorCommitments(recordId, token)
      ])
      if (cancelled) return
      if (!detailRes.data) {
        setNotFound(detailRes.notFound === true)
        setLoadError(detailRes.error ?? null)
        setLoading(false)
        return
      }
      applyDetail(detailRes.data)
      if (commitmentsRes.error) showErrorToast(commitmentsRes.error)
      setCommitments(commitmentsRes.data)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNew, prefillAccountId, formTabKey, dispatch, applyDetail])

  const fieldOptions = useMemo<InvestorFieldOptions>(
    () => ({
      classificationOptions: (classifications ?? []).map((c) => ({ value: c.id, label: c.label })),
      sbicInvestorTypeOptions: (sbicInvestorTypes ?? []).map((t) => ({ value: t.id, label: t.label })),
      legalEntityTypeOptions: formOptions?.legal_entity_types ?? EMPTY_OPTIONS,
      noticeSupplementOptions: formOptions?.notice_supplements ?? EMPTY_OPTIONS
    }),
    [classifications, sbicInvestorTypes, formOptions]
  )

  const accountLocked = prefillAccountId != null
  const investorFields = useMemo(() => getInvestorFields(draft, { isNew, accountLocked }), [draft, isNew, accountLocked])
  // an existing investor's account: a link, not an input (the baseline's -
  // nothing edits it)
  const accountLinkFields = useMemo(() => (isNew ? [] : getAccountLinkFields(baseline)), [isNew, baseline])
  const classificationFields = useMemo(() => getClassificationFields(draft, fieldOptions), [draft, fieldOptions])
  const noticeFields = useMemo(() => getNoticeFields(draft, fieldOptions), [draft, fieldOptions])

  const dirty = useMemo(() => {
    if (isNew) {
      return (
        text(draft.investors__name) !== text(baseline.investors__name) ||
        (draft.accounts__id?.value ?? null) !== (baseline.accounts__id?.value ?? null) ||
        Object.keys(buildInvestorCreatePayload(draft)).length > 2
      )
    }
    return detail != null && Object.keys(buildInvestorEditPayload(detail.investors__id, draft, baseline)).length > 1
  }, [isNew, draft, baseline, detail])

  const handleChange = useCallback((key: string, value: FieldValue) => {
    setDraft((prev) => {
      switch (key) {
        case InvestorDetailFields.accounts__id: {
          const account = toAccountValue(value)
          // a new investor entity is usually named after its account
          return {
            ...prev,
            accounts__id: account,
            investors__name: !nameTouchedRef.current && account?.label ? account.label : prev.investors__name
          }
        }
        case InvestorDetailFields.prog__notice_supplement_template_ids:
          return {
            ...prev,
            prog__notice_supplement_template_ids: Array.isArray(value)
              ? value.map((v) => Number(typeof v === 'object' ? v.value : v)).filter((v) => Number.isFinite(v))
              : []
          }
        case InvestorDetailFields.investor_classifications__id:
        case InvestorDetailFields.legal_entity_types__id:
        case InvestorDetailFields.sbic_investor_types__id:
          return { ...prev, [key]: value == null || value === '' ? null : Number(value) }
        case InvestorDetailFields.investors__name:
          nameTouchedRef.current = true
          return { ...prev, [key]: value == null ? null : String(value) }
        case InvestorDetailFields.investors__legal_name:
        case InvestorDetailFields.investors__investor_number:
          return { ...prev, [key]: value == null ? null : String(value) }
        default:
          return prev
      }
    })
    setValidationErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  /** Cancel: an edit goes back to what was loaded, a new investor's tab closes. */
  const handleCancel = useCallback(() => {
    dismissErrorToast(VALIDATION_TOAST_KEY)
    if (isNew) {
      closeFormTab(formTabKey)
      return
    }
    setDraft(baseline)
    setValidationErrors({})
    setResetVersion((v) => v + 1)
  }, [isNew, closeFormTab, formTabKey, baseline])

  const handleClose = useCallback(() => {
    closeFormTab(formTabKey)
  }, [closeFormTab, formTabKey])

  const handleSave = useCallback(async () => {
    // Required-field gate: per-field red text + one toast naming the fields,
    // and the InputManager scrolls to and focuses the first bad field.
    const errors: Record<string, string> = {}
    const required: FieldConfig[] = investorFields.filter((field) => field.required)
    required.forEach((field) => {
      const value = (draft as unknown as Record<string, unknown>)[field.key]
      if (!isRequiredFieldValid(value)) errors[field.key] = `${field.label || field.key} is required`
    })
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      setValidationErrorsVersion((v) => v + 1)
      showErrorToast(summariseFieldErrors([errors]) ?? 'Please complete the required fields', {
        key: VALIDATION_TOAST_KEY
      })
      return
    }
    dismissErrorToast(VALIDATION_TOAST_KEY)

    const payload = isNew
      ? buildInvestorCreatePayload(draft)
      : detail
        ? buildInvestorEditPayload(detail.investors__id, draft, baseline)
        : null
    if (!payload) return

    setSaving(true)
    try {
      const res = await saveInvestor(payload, getAccessToken(store.getState().auth))
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to save the investor')
        return
      }
      const saved = res.data
      if (isNew) {
        toast.success('Investor created')
        // Morph the "new" tab into the edit tab in place; the pane remounts
        // on the new key and cold-loads the investor.
        dispatch(
          transformFormTab({
            key: formTabKey,
            module: TABLE_INVESTORS,
            recordId: saved.investors__id,
            title: saved.investors__name
          })
        )
        navigate(`/${TABLE_INVESTORS}/forms/full/${saved.investors__id}`, { replace: true })
        return
      }
      toast.success('Investor saved')
      applyDetail(saved)
      // the documents a commitment carries may follow the investor's
      void loadCommitments()
    } finally {
      setSaving(false)
    }
  }, [investorFields, draft, isNew, detail, baseline, dispatch, formTabKey, navigate, applyDetail, loadCommitments])

  return {
    detail,
    commitments,
    commitmentsLoading,
    loading,
    notFound,
    loadError,
    investorFields,
    accountLinkFields,
    classificationFields,
    noticeFields,
    dirty,
    saving,
    validationErrors,
    validationErrorsVersion,
    resetVersion,
    handleChange,
    handleCancel,
    handleClose,
    handleSave,
    loadCommitments
  }
}
